import { useEffect, useMemo, useState } from 'react'
import { useGetFubUsers } from '@forwardflow/queries/onboarding.queries'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import useAuthUser from '@forwardflow/hooks/customHooks/useAuthUser'
import { TFubUser } from '@forwardflow/types/onboarding'
import { Screen } from '../frame/SetupFrame'
import { useStepFooter } from '../../hooks/useStepFooter'
import {
   Avatar,
   Button,
   IconDot,
   Input,
   LinkButton,
   Row,
   RowCard,
   SearchPick,
   Skeleton,
   Spinner,
} from '../ui/primitives'
import { e164, isUsPhone, prettyPhone } from '../../utils/format'
import { fullName, membersOf, ownerOf, pickOwner } from '../../utils/seats'

/** How long the team may take to arrive before the sign-in details are offered instead. */
const PATIENCE_MS = 15000
/** How often to ask again while the team is still importing. */
const POLL_MS = 5000

/**
 * Step 2: who you are, from Follow Up Boss.
 *
 * The signed-in person's FUB user (email match, else the account owner) is
 * shown with their mobile number, which is where leads are handed to them
 * and what the calling area code is matched to. Both can be changed here.
 *
 * A brand-new account has just handed over its key, and the team can take a
 * little while to import: the list is polled, and after a short wait the
 * sign-in details are offered so nobody is stuck. A FUB user picked later
 * (from Settings) replaces that entry.
 */
export const YouStep = () => {
   const { onboardingData, getNextStep, setInvitedUsers } =
      useOnboardingStoreV2()
   const { organizationId, isFUBConnected, invitedUsers, email } =
      onboardingData
   const { data: me } = useAuthUser({})
   const { data, isLoading } = useGetFubUsers(
      organizationId ?? '',
      1000,
      1,
      isFUBConnected,
      {
         refreshInterval: (latest) => (latest?.data?.length ? 0 : POLL_MS),
         revalidateOnFocus: true,
      }
   )
   const users: TFubUser[] = useMemo(() => data?.data || [], [data])
   const saved = ownerOf(invitedUsers)

   const [ownerId, setOwnerId] = useState<string | null>(
      saved ? String(saved.id) : null
   )
   const [phone, setPhone] = useState(saved?.phone || '')
   const [editing, setEditing] = useState(false)
   const [draft, setDraft] = useState('')
   const [picking, setPicking] = useState(false)
   const [manual, setManual] = useState(
      Boolean(saved && Number(saved.id) === 0)
   )
   const [waited, setWaited] = useState(false)

   // Match the signed-in person once the list arrives.
   useEffect(() => {
      if (ownerId || !users.length) return
      const found = pickOwner(users, email)
      if (found) {
         setOwnerId(String(found.id))
         if (!phone) setPhone(found.phone || '')
      }
   }, [users, email, ownerId, phone])

   // After a while with no team, offer the sign-in details instead.
   useEffect(() => {
      if (users.length) return
      const t = setTimeout(() => setWaited(true), PATIENCE_MS)
      return () => clearTimeout(t)
   }, [users.length])

   const meUser: TFubUser | null = useMemo(() => {
      if (!me?.email && !email) return null
      const first = me?.firstName || ''
      const last = me?.lastName || ''
      return {
         id: 0,
         crmUserId: 0,
         name: `${first} ${last}`.trim() || me?.email || email || '',
         firstName: first,
         lastName: last,
         email: me?.email || email || '',
         phone: '',
         roles: [],
         created: '',
         updated: '',
         isOwner: true,
      }
   }, [me?.email, me?.firstName, me?.lastName, email])

   const fromList = users.find((u) => String(u.id) === ownerId)
   const owner = manual
      ? saved && Number(saved.id) === 0
         ? saved
         : meUser
      : fromList || saved
   const matched =
      Boolean(owner?.email) &&
      (owner?.email || '').toLowerCase() === (email || '').toLowerCase()
   const phoneOk = isUsPhone(phone)

   const choose = (id: string) => {
      const picked = users.find((u) => String(u.id) === id)
      if (!picked) return
      setManual(false)
      setOwnerId(id)
      setPhone(picked.phone || '')
      setPicking(false)
   }
   const useMyDetails = () => {
      setManual(true)
      setPicking(false)
      setDraft('')
      setEditing(true)
   }
   const savePhone = () => {
      if (!isUsPhone(draft)) return
      setPhone(e164(draft))
      setEditing(false)
   }
   const confirm = () => {
      if (!owner || !phoneOk || editing) return
      const members = membersOf(invitedUsers).filter(
         (u) => String(u.id) !== String(owner.id)
      )
      setInvitedUsers([
         ...members.map((u) => ({ ...u, isOwner: false })),
         { ...owner, phone: e164(phone), isOwner: true },
      ])
      getNextStep()
   }

   const stillImporting = !users.length && !saved && !manual
   useStepFooter(
      stillImporting
         ? waited
            ? 'Use my sign-in details instead'
            : 'Reading your team from Follow Up Boss'
         : !owner
           ? 'Pick your account to continue'
           : editing
             ? 'Save your number first'
             : phoneOk
               ? 'Yes, that’s me'
               : 'Add your mobile number first',
      stillImporting ? (waited ? useMyDetails : () => undefined) : confirm,
      {
         disabled: stillImporting ? !waited : !owner || !phoneOk || editing,
         hint: stillImporting
            ? 'Follow Up Boss is sending your team over. This usually takes a moment.'
            : 'Interested leads are transferred to this person, on this number.',
         testId: 'confirm-you',
      }
   )

   const pickerItems = users.map((p) => ({
      id: String(p.id),
      label: fullName(p),
      sub: p.email,
   }))

   return (
      <Screen
         stepKey="you"
         kind="you"
         title="Is this you?"
         lede={
            manual
               ? 'Your Follow Up Boss team is still importing, so this is you from your sign-in. Add the mobile number we hand leads to.'
               : matched
                 ? 'We matched your Follow Up Boss user to your sign-in. Check the name and the mobile number we hand leads to.'
                 : 'Pick your own Follow Up Boss user. Interested leads are handed to this person, on this mobile number.'
         }
         width="sm"
      >
         {isLoading && !users.length && !saved && !manual ? (
            <Skeleton className="h-[148px] rounded-[20px]" />
         ) : stillImporting ? (
            <RowCard testId="team-importing">
               <Row>
                  <span className="text-[var(--ob-light)]">
                     <Spinner size={22} />
                  </span>
                  <div className="flex-1 min-w-0">
                     <p className="text-[15px] font-semibold">
                        Reading your team from Follow Up Boss
                     </p>
                     <p className="text-[13px] text-[var(--ob-text-2)]">
                        {waited
                           ? 'Taking longer than usual. You can continue with your sign-in details; the team arrives in the background.'
                           : 'Your users, stages and tags are on their way.'}
                     </p>
                  </div>
                  {waited && (
                     <LinkButton onClick={useMyDetails} testId="use-my-details">
                        Use my details
                     </LinkButton>
                  )}
               </Row>
            </RowCard>
         ) : !owner ? (
            <div data-testid="owner-picker">
               <SearchPick
                  items={pickerItems}
                  placeholder="Search your Follow Up Boss users"
                  emptyLabel="No user matches"
                  onPick={choose}
                  testId="pick-owner"
               />
            </div>
         ) : (
            <RowCard testId="owner-card">
               <Row>
                  <Avatar name={fullName(owner)} size={40} />
                  <div className="flex-1 min-w-0">
                     <p
                        className="text-[15px] font-semibold truncate"
                        data-testid="owner-name"
                     >
                        {fullName(owner)}
                     </p>
                     <p className="text-[13px] text-[var(--ob-text-2)] truncate">
                        {owner.email}
                        {manual
                           ? ' · from your sign-in'
                           : owner.isOwner && Number(owner.id) !== 0
                             ? ' · Account owner'
                             : ''}
                     </p>
                  </div>
                  {users.length > 0 && (
                     <LinkButton
                        onClick={() => setPicking((v) => !v)}
                        testId="not-me"
                     >
                        {picking ? 'Cancel' : 'Not me'}
                     </LinkButton>
                  )}
               </Row>
               <Row>
                  <IconDot name="phone" tone="purple" />
                  {editing ? (
                     <div className="flex-1 flex flex-col sm:flex-row sm:items-end gap-2 py-3">
                        <Input
                           label="Mobile number"
                           value={draft}
                           onChange={setDraft}
                           placeholder="(719) 555-0100"
                           autoFocus
                           onEnter={savePhone}
                           testId="phone-input"
                           className="flex-1"
                           inputMode="tel"
                        />
                        <div className="flex gap-2">
                           <Button
                              onClick={savePhone}
                              disabled={!isUsPhone(draft)}
                              testId="save-phone"
                           >
                              Save
                           </Button>
                           {(phone || !manual) && (
                              <Button
                                 variant="secondary"
                                 onClick={() => setEditing(false)}
                              >
                                 Cancel
                              </Button>
                           )}
                        </div>
                     </div>
                  ) : (
                     <>
                        <div className="flex-1 min-w-0">
                           {phone ? (
                              <p
                                 className="text-[15px] font-semibold"
                                 data-testid="owner-phone"
                              >
                                 {prettyPhone(phone)}
                                 {!phoneOk && (
                                    <span className="ml-2 text-[12px] font-normal text-[var(--ob-warn)]">
                                       not a US number
                                    </span>
                                 )}
                              </p>
                           ) : (
                              <p className="text-[15px] text-[var(--ob-text-3)]">
                                 No mobile number on file
                              </p>
                           )}
                           <p className="text-[13px] text-[var(--ob-text-2)]">
                              Mobile · where we transfer leads
                           </p>
                        </div>
                        <LinkButton
                           onClick={() => {
                              setDraft(phone ? prettyPhone(phone) : '')
                              setEditing(true)
                           }}
                           testId="change-phone"
                        >
                           {phone ? 'Not my number' : 'Add a number'}
                        </LinkButton>
                     </>
                  )}
               </Row>
            </RowCard>
         )}
         {owner && picking && users.length > 0 && (
            <div className="mt-3" data-testid="owner-picker">
               <SearchPick
                  items={pickerItems}
                  placeholder="Search your Follow Up Boss users"
                  emptyLabel="No user matches"
                  onPick={choose}
                  testId="pick-owner"
                  renderItem={(r) => (
                     <>
                        <Avatar name={r.label} size={28} />
                        <span className="flex-1 min-w-0 truncate">
                           {r.label}
                        </span>
                        <span className="text-[12px] text-[var(--ob-text-3)] truncate">
                           {r.sub}
                        </span>
                     </>
                  )}
               />
            </div>
         )}
         {manual && users.length > 0 && (
            <p className="mt-3 text-center text-[13px] text-[var(--ob-text-2)]">
               Your team has arrived.{' '}
               <LinkButton
                  onClick={() => setPicking(true)}
                  testId="pick-from-team"
               >
                  Pick your Follow Up Boss user instead
               </LinkButton>
            </p>
         )}
      </Screen>
   )
}
